import { lazy, Suspense, type ReactNode } from "react";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { WhatsAppWidget } from "./WhatsAppWidget";
import { MobileActionBar } from "./MobileActionBar";
import { Toaster } from "@/components/ui/sonner";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { useStoreCart } from "@/context/StoreCartContext";

const CartDrawer = lazy(() => import("./CartDrawer").then((m) => ({ default: m.CartDrawer })));
const StoreCartDrawer = lazy(() =>
  import("./StoreCartDrawer").then((m) => ({ default: m.StoreCartDrawer })),
);

export function SiteShell({ children }: { children: ReactNode }) {
  const { dir } = useI18n();
  const { isOpen: isQuoteCartOpen } = useCart();
  const { isOpen: isStoreCartOpen } = useStoreCart();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      {/* The bar is fixed, so the page needs to end above it or the last
          element on every page sits underneath. Phones only, matching the bar. */}
      <main className="flex-1 pb-[4.75rem] md:pb-0">{children}</main>
      <SiteFooter />
      {/* CartDrawer (legacy "quote cart") stays mounted so /cart still works
          standalone — see CartContext.tsx's top-of-file note. FloatingQuoteCTA
          is intentionally NOT mounted here anymore: it was the last persistent
          entry point into that quote-cart from every page. */}
      <Suspense fallback={null}>
        {isQuoteCartOpen && <CartDrawer />}
        {isStoreCartOpen && <StoreCartDrawer />}
      </Suspense>
      <WhatsAppWidget />
      {/* Phones get the bar instead of the floating pill — see WhatsAppWidget,
          which now hides itself below md. Two WhatsApp entry points stacked in
          the same corner is worse than either alone. */}
      <MobileActionBar />
      {/* Toasts belong in the corner the reader's eye starts from, which is the
          right in Arabic and Persian. `position` takes physical corners, so this
          is one of the few places direction has to be branched on explicitly. */}
      <Toaster position={dir === "rtl" ? "top-left" : "top-right"} />
    </div>
  );
}
